import PlatformBaseController from "./platform_base_controller";

// Main controller for handling project/document list filtering
export default class extends PlatformBaseController {
    static targets = [
        "filterForm",
        "filterSubmit",
        "logStartDate",
        "logEndDate"
    ];

    static values = {
        projectId: { type: String,  default: undefined },
        logId:     { type: Number,  default: undefined },
        filterTag: { type: String,  default: undefined },
        initDates: { type: Boolean, default: true } // server can toggle
    };

    connect() {
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;
        this.skipDefaultDates = false;
        this.bootstrapInitialDates();
    }

    filterFormTargetConnected(target) {
        this.bootstrapInitialDates(target);
    }

    bootstrapInitialDates(form = null) {
        const forms      = this.allFilterForms(form);
        const primary    = forms.find((scope) => scope?.querySelector?.('[name="metrics_start_date"]')) || forms[0];
        if (!primary) return;

        if (!this.shouldInitializeDates(primary)) {
            const start = primary.querySelector?.('[name="metrics_start_date"]')?.value || "";
            const end   = primary.querySelector?.('[name="metrics_end_date"]')?.value   || "";
            this.initialDates = { startYMD: start, endYMD: end };
            return;
        }

        const { startYMD, endYMD } = this.ensureFilterDates({ form: primary });
        this.initialDates = { startYMD, endYMD };
    }

    allFilterForms(preferred = null) {
        const forms = [];
        const push = (candidate) => {
            if (candidate && !forms.includes(candidate)) forms.push(candidate);
        };

        push(preferred);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach(push);
        push(this.element.closest?.("form"));
        push(this.element);
        return forms;
    }

    shouldInitializeDates(form = null) {
        const datasetKey = this.datasetKeyFor("InitDatesValue");
        const sources    = [];

        if (form) sources.push(form.dataset?.[datasetKey]);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach((target) => sources.push(target.dataset?.[datasetKey]));
        sources.push(this.element.dataset?.[datasetKey]);

        for (const value of sources) {
            const parsed = this.parseBooleanDataset(value);
            if (parsed !== undefined) return parsed;
        }

        return true;
    }

    parseBooleanDataset(value) {
        if (value === undefined || value === null || value === "") return undefined;
        if (typeof value === "boolean") return value;

        const normalised = String(value).toLowerCase();
        if (["false", "0", "no", "off"].includes(normalised)) return false;
        if (["true", "1", "yes", "on"].includes(normalised)) return true;
        return undefined;
    }

    datasetKeyFor(suffix) {
        return `${this.identifierCamelCase}${suffix}`;
    }

    get identifierCamelCase() {
        if (!this._identifierCamelCase) {
            this._identifierCamelCase = this.identifier.replace(/-([a-z])/g, (_m, chr) => chr.toUpperCase());
        }
        return this._identifierCamelCase;
    }

    setDates({ startYMD, endYMD }, form = null) {
        const forms = this.allFilterForms(form);

        forms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;
            const startInput = scope.querySelector('[name="metrics_start_date"]');
            const endInput   = scope.querySelector('[name="metrics_end_date"]');

            if (startInput) startInput.value = startYMD != null ? startYMD : "";
            if (endInput)   endInput.value   = endYMD   != null ? endYMD   : "";
        });

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = startYMD != null ? startYMD : ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = endYMD   != null ? endYMD   : ""; });

        if (startYMD !== undefined) this.setPersistentFilter("metrics_start_date", startYMD != null ? startYMD : "");
        if (endYMD   !== undefined) this.setPersistentFilter("metrics_end_date",   endYMD   != null ? endYMD   : "");
    }

    ensureFilterDates({ event = null, form = null, period = 30 } = {}) {
        const fallbackForm =
            event?.target?.closest?.("form") ||
            event?.target?.form ||
            (this.hasFilterFormTarget ? this.filterFormTargets[0] : null) ||
            this.element.closest?.("form") ||
            this.element;

        const scopeForms = this.allFilterForms(form || fallbackForm);

        const today        = this.startOfLocalDay(new Date());
        const defaultEnd   = this.formatYMD(today);
        const defaultStart = this.formatYMD(this.addDays(today, -period));

        let resolvedStart = "";
        let resolvedEnd   = "";

        scopeForms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;

            const startInput = scope.querySelector('[name="metrics_start_date"]');
            const endInput   = scope.querySelector('[name="metrics_end_date"]');

            if (startInput) {
                if (!startInput.value) startInput.value = defaultStart;
                if (!resolvedStart && startInput.value) resolvedStart = startInput.value;
            }

            if (endInput) {
                if (!endInput.value) endInput.value = defaultEnd;
                if (!resolvedEnd && endInput.value) resolvedEnd = endInput.value;
            }
        });

        if (resolvedStart) this.setPersistentFilter("metrics_start_date", resolvedStart);
        if (resolvedEnd)   this.setPersistentFilter("metrics_end_date",   resolvedEnd);

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = resolvedStart || ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = resolvedEnd   || ""; });

        return {
            startYMD: resolvedStart || "",
            endYMD:   resolvedEnd   || "",
            start:    resolvedStart ? this.toLocalDateOnly(resolvedStart) : undefined,
            end:      resolvedEnd ? this.toLocalDateOnly(resolvedEnd)   : undefined
        };
    }
    // Handles project select dropdown changes
    selectProject(event) {
        const projectId = event.target.value;
        this.setPersistentFilter("project_id", projectId);
        this.updateProjectIdInputs(projectId);
        this.fetchAction(`/insights/metrics/filter_parameters?project_id=${encodeURIComponent(projectId)}`);
    }

    updateProjectIdInputs(projectId) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const hidden = form.querySelector('[name="project_id"]');
            if (hidden) hidden.value = projectId;
        });
    }

    syncDateInputs(fieldName, value, exclude = null) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const input = form.querySelector(`[name="${fieldName}"]`);
            if (input && input !== exclude) input.value = value;
        });
    }

    removeFilterTag(event) {
        event.preventDefault();

        const clickedElement = event.currentTarget;
        const filterTag      = clickedElement.dataset.filterTagValue;

        if (filterTag) {
            this.deletePersistentFilter(filterTag);
            this.removeParam(filterTag);
        }

        // Reflect emptiness immediately in the mirrored inputs
        if (filterTag === "metrics_start_date") {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("metrics_start_date", "");
            this.skipDefaultDates = true;
        }
        if (filterTag === "metrics_end_date") {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("metrics_end_date", "");
            this.skipDefaultDates = true;
        }

        clickedElement.remove();
    }

    clearFilters(event) {
        event.preventDefault();

        const form = event.target.closest("form") || event.target.form || this.element;

        const clickedElement = event.currentTarget;
        const filterTag      = clickedElement?.dataset?.filterTagValue;
        const projectId      = clickedElement?.dataset?.projectIdValue;

        const search = this.getSearch(form);

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        // Start from current URL params (so we can remove keys that might be present there)
        const url           = new URL(window.location.href);
        const currentParams = new URLSearchParams(url.search);

        ["project_id", "search", "filterTag"].forEach((k) => currentParams.delete(k));

        if (filterTag) {
            this.deletePersistentFilter(filterTag);
            this.removeParam(filterTag);
            currentParams.delete(filterTag);
        }

        // Restore inputs & cookies to default window
        const today = this.startOfLocalDay(new Date());
        const defaultEnd = this.formatYMD(today);
        const defaultStart = this.formatYMD(this.addDays(today, -30));
        const initial = { startYMD: defaultStart, endYMD: defaultEnd };
        this.setDates(initial, form);
        this.skipDefaultDates = false;
        this.skipDefaultDates = true;

        // Build request
        const params = new URLSearchParams();
        if (search)   params.set("search", search);
        if (projectId) params.set("project_id", projectId);
        if (initial.startYMD) params.set("metrics_start_date", initial.startYMD);
        if (initial.endYMD)   params.set("metrics_end_date", initial.endYMD);

        this.fetchAction(`/insights/metrics/clear_filters?${params.toString()}`);
    }

    setFilters(event) {
        event.preventDefault();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;

        const form      = event.target.closest?.("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");
        const search    = this.getSearch(form);
        const s         = this.getPersistedFilter(form, "metrics_start_date");
        const e         = this.getPersistedFilter(form, "metrics_end_date");
        let startValue  = s;
        let endValue    = e;

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (s === undefined) {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("metrics_start_date", "");
        }
        if (e === undefined) {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value   = ""; });
            this.syncDateInputs("metrics_end_date", "");
        }

        if (!this.skipDefaultDates && this.shouldInitializeDates(form)) {
            const ensured = this.ensureFilterDates({ form });
            startValue = ensured.startYMD;
            endValue   = ensured.endYMD;
        }

        const params = new URLSearchParams();
        if (search)   params.append("search", search);
        if (projectId) params.append("project_id", projectId);
        if (startValue !== undefined) params.append("metrics_start_date", startValue || "");
        if (endValue   !== undefined) params.append("metrics_end_date",   endValue   || "");

        this.fetchAction(`/insights/metrics/filter_parameters?${params.toString()}`);
    }

    updateFilterDate(event) {
        const input = event.target;
        const { name, value } = input;
        if (name === "metrics_start_date" || name === "metrics_end_date") {
            if (value) {
                this.setPersistentFilter(name, value);
            } else {
                this.deletePersistentFilter(name);
            }

            if (name === "metrics_start_date") {
                if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }

            if (name === "metrics_end_date") {
                if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }
        }
    }

}
